/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, OverlayArrow as OverlayArrowBase, Provider, Tooltip as TooltipCore, type TooltipProps as TooltipCoreProps, useContextProps } from 'react-aria-components';
import { zIndices } from '../../constants.stylex';
import { NotificationErrorOutlineFillIcon } from '../../icons/notification-error-outline-fill/notification-error-outline-fill.icon';
import { $ } from '../../tokens.stylex';
import { Text, TextContext } from '../text/text.component';

interface TooltipProps extends Omit<TooltipCoreProps, 'style' | 'className'> {
  /** Whether the tooltip is attentive. (Default: false) */
  isAttentive?: boolean;
  style?: stylex.StyleXStyles;
}

const TooltipContext = createContext<ContextValue<TooltipProps, HTMLDivElement>>({});

const Tooltip = forwardRef((props: TooltipProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TooltipContext);

  const { style, isAttentive = false, placement = 'top', ...coreProps } = props;
  let { children } = coreProps;

  return (
    <TooltipCore
      ref={ref}
      {...stylex.props(
        styles.base,
        (placement.includes('top') && positionVariants.top) ||
        (placement.includes('bottom') && positionVariants.bottom) ||
        (placement.includes('left') && positionVariants.left) ||
        (placement.includes('right') && positionVariants.right),
        isAttentive && styles.attentive,
        style)}
      placement={placement}
      {...coreProps}>
      {(renderProps) => {
        children = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(children).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              color: isAttentive ? 'critical' : 'inverted',
              size: 14
            }],
          ]}>
            <OverlayArrowBase {...stylex.props(
              arrowStyles.base,
              (placement.includes('top') && arrowStyles.top) ||
              (placement.includes('bottom') && arrowStyles.bottom) ||
              (placement.includes('left') && arrowStyles.left) ||
              (placement.includes('right') && arrowStyles.right))}>
              <svg
                viewBox="0 0 13 6"
                fill="none"
                {...stylex.props(
                  iconStyles.base,
                  isAttentive && iconStyles.attentive,
                  (placement.includes('top') && iconPositionStyles.top) ||
                  (placement.includes('bottom') && iconPositionStyles.bottom) ||
                  (placement.includes('left') && iconPositionStyles.left) ||
                  (placement.includes('right') && iconPositionStyles.right)
                )}>
                <path d="M7.56066 5.50991C6.97487 6.16336 6.02513 6.16336 5.43934 5.50991L0.499999 1.04907e-06L12.5 0L7.56066 5.50991Z" fillOpacity="0.96" />
              </svg>
            </OverlayArrowBase>
            {isAttentive && <NotificationErrorOutlineFillIcon color="critical" size={12} />}
            {isTextOnly ? <Text>{children}</Text> : children}
          </Provider>
        );
      }}
    </TooltipCore>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    backdropFilter: 'blur(12px)',
    backgroundColor: $.opacity96,
    borderRadius: 6,
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    forcedColorAdjust: 'none',
    gap: 6,
    justifyContent: 'center',
    minHeight: 30,
    opacity: {
      default: 1,
      ':is([data-entering], [data-exiting])': 0,
    },
    outline: 'none',
    paddingBlock: 6,
    paddingInline: 12,
    transitionDuration: '200ms',
    transitionProperty: 'transform, opacity',
    zIndex: zIndices.tooltip,
  },
  attentive: { backgroundColor: $.surfaceCriticalMedium },
});

const positionVariants = stylex.create({
  top: {
    marginBottom: 8,
    transform: {
      default: 'translate3d(0, 0, 0)',
      ':is([data-entering], [data-exiting])': 'translateY(4px)',
    }
  },
  bottom: {
    marginTop: 8,
    transform: {
      default: 'translate3d(0, 0, 0)',
      ':is([data-entering], [data-exiting])': 'translateY(-4px)',
    },
  },
  left: {
    marginRight: 8,
    transform: {
      default: 'translate3d(0, 0, 0)',
      ':is([data-entering], [data-exiting])': 'translateX(4px)',
    },
  },
  right: {
    marginLeft: 8,
    transform: {
      default: 'translate3d(0, 0, 0)',
      ':is([data-entering], [data-exiting])': 'translateX(-4px)',
    },
  },
});

const arrowStyles = stylex.create({
  base: {
    display: 'flex',
    height: 6,
    width: 13,
  },
  left: {
    marginLeft: -4,
  },
  right: {
    marginRight: -4,
  },
  top: {},
  bottom: {},
});

const iconStyles = stylex.create({
  base: { fill: $.opacity96 },
  attentive: { fill: $.surfaceCriticalMedium },
});

const iconPositionStyles = stylex.create({
  top: {},
  bottom: {
    transform: 'rotate(180deg)',
  },
  left: {
    transform: 'rotate(-90deg)',
  },
  right: {
    transform: 'rotate(90deg)',
  },
});

export { Tooltip, TooltipContext, type TooltipProps };
